import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Share, Facebook, Twitter, Hash, MessageSquare, Link } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { formatTargetCode } from '@/utils/targetCodeUtils';

interface HistoricTargetHeaderProps {
  targetCode: string;
  returnPage?: number;
}

const HistoricTargetHeader: React.FC<HistoricTargetHeaderProps> = ({ 
  targetCode,
  returnPage = 1
}) => {
  const navigate = useNavigate();
  
  const formattedCode = useMemo(() => formatTargetCode(targetCode), [targetCode]);
  const shareUrl = useMemo(() => window.location.href, []);

  const handleBackClick = useCallback(() => {
    navigate(`/history?page=${returnPage}`);
  }, [navigate, returnPage]);

  const copyUrl = useCallback(async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success('URL copied to clipboard');
  }, [shareUrl]);

  const shareToFacebook = useCallback(() => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  }, [shareUrl]);

  const shareToX = useCallback(() => {
    const text = encodeURIComponent(`Check out my RemoteViz session: Target ${formattedCode} #RemoteViewing`);
    const url = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  }, [formattedCode, shareUrl]);

  const shareToReddit = useCallback(() => {
    const title = encodeURIComponent(`My RemoteViz Session: Target ${formattedCode}`);
    const url = `https://reddit.com/submit?title=${title}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank');
  }, [formattedCode, shareUrl]);

  const shareToDiscord = useCallback(() => {
    navigator.clipboard.writeText(`Check out my RemoteViz session: Target ${formattedCode}\n${shareUrl}`);
    toast.success('Copied to clipboard - paste in Discord');
  }, [formattedCode, shareUrl]);

  return (
    <div className="flex items-center justify-between p-4 border-b border-border bg-card/50">
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={handleBackClick}
          className="hover:bg-muted"
        >
          <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          <span className="sr-only">Back to History</span>
        </Button>
        <div>
          <h1 className="text-xl font-semibold">Target Code: {formattedCode}</h1>
          <p className="text-sm text-muted-foreground">Historic Session</p>
        </div>
      </div>

      <TooltipProvider>
        <Tooltip>
          <DropdownMenu>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-muted">
                  <Share className="h-5 w-5 text-muted-foreground" />
                  <span className="sr-only">Share session</span>
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Share session</p>
            </TooltipContent>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={copyUrl} className="cursor-pointer">
                <Link className="mr-2 h-4 w-4" />
                Copy URL
              </DropdownMenuItem>
              <DropdownMenuItem onClick={shareToFacebook} className="cursor-pointer">
                <Facebook className="mr-2 h-4 w-4" />
                Facebook
              </DropdownMenuItem>
              <DropdownMenuItem onClick={shareToX} className="cursor-pointer">
                <Twitter className="mr-2 h-4 w-4" />
                X (Twitter)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={shareToReddit} className="cursor-pointer">
                <Hash className="mr-2 h-4 w-4" />
                Reddit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={shareToDiscord} className="cursor-pointer">
                <MessageSquare className="mr-2 h-4 w-4" />
                Discord
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default HistoricTargetHeader;